import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Textarea, Badge } from '@artosera/ui';
import { formatDate, type PartageEspaceReponse } from '@artosera/shared';
import {
  espaceCollectionneurApi,
  useContactEspace,
  useActionEspace,
  type GesteAcces,
} from '../../api/espaceCollectionneur.js';
import { galerieALaction } from '../../auth/sujet.js';
import { estApiError } from '../../api/errors.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { PartagerDialog } from './PartagerDialog.js';
import styles from './EspaceCollectionneur.module.css';

export function EspaceDuContact({
  contactId,
  emails,
}: {
  contactId: string;
  emails: readonly string[];
}) {
  const { galerie, galerieSlug } = useShellContext();
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const id = useId();
  const contact = useContactEspace(galerieSlug, contactId);
  const [partager, setPartager] = useState(false);
  const [geste, setGeste] = useState<'suspendre' | 'revoquer' | null>(null);
  const [motif, setMotif] = useState('');
  const [confirme, setConfirme] = useState(false);
  const peutEcrire = galerieALaction(galerie, 'collector-space:write');
  const invitation = useActionEspace(galerieSlug, (_: void, appel) =>
    espaceCollectionneurApi.inviter(galerieSlug, contactId, appel),
  );
  const action = useActionEspace(
    galerieSlug,
    (corps: { geste: GesteAcces; motif?: string }, appel) =>
      espaceCollectionneurApi.geste(
        galerieSlug,
        contact.data!.acces!.id,
        corps.geste,
        { version: contact.data!.acces!.version, ...(corps.motif ? { motif: corps.motif } : {}) },
        appel,
      ),
  );
  const retrait = useActionEspace(galerieSlug, (partageId: string, appel) =>
    espaceCollectionneurApi.retirer(galerieSlug, partageId, appel),
  );
  const date = (valeur: string | null) =>
    valeur
      ? formatDate(new Date(valeur), i18n.language === 'en' ? 'en' : 'fr')
      : t('espaceCollectionneur.jamais');
  if (contact.isLoading) return <p role="status">{t('commun:chargement')}</p>;
  if (!contact.data) return <p role="alert">{t('commun:erreurReseau')}</p>;
  const { acces, partages, ecartEmail } = contact.data;
  const groupes = new Map<string, PartageEspaceReponse[]>();
  for (const p of partages) {
    const cle = p.oeuvreId ?? '';
    groupes.set(cle, [...(groupes.get(cle) ?? []), p]);
  }
  function liste(elements: PartageEspaceReponse[]) {
    return (
      <ul className={styles.liste}>
        {elements.map((p) => (
          <li key={p.id}>
            <strong>{p.libelle ?? t('espaceCollectionneur.libelleMasque')}</strong> ·{' '}
            {t(`espaceCollectionneur.types.${p.type}`)}
            <p>{t('espaceCollectionneur.partageLe', { date: date(p.partageLe) })}</p>
            <p>
              {t('espaceCollectionneur.premiereConsultation', {
                date: date(p.premiereConsultationLe),
              })}
            </p>
            <p>
              {t('espaceCollectionneur.derniereConsultation', {
                date: date(p.derniereConsultationLe),
              })}
            </p>
            {!p.visible && (
              <p>
                {t('espaceCollectionneur.masque', {
                  raison: t(`espaceCollectionneur.raisons.${p.raison ?? 'condition_tombee'}`),
                })}
              </p>
            )}
            {peutEcrire && !p.retireLe && (
              <Button
                variant="secondary"
                loading={retrait.isPending}
                onClick={() => retrait.mutate(p.id)}
              >
                {t('espaceCollectionneur.retirer')}
              </Button>
            )}
          </li>
        ))}
      </ul>
    );
  }
  const erreur = invitation.error ?? action.error ?? retrait.error;
  return (
    <section className={styles.bloc} aria-labelledby={`${id}-titre`}>
      <h2 id={`${id}-titre`}>{t('espaceCollectionneur.titre')}</h2>
      <Badge>{t(`espaceCollectionneur.etats.${acces?.etat ?? 'aucun'}`)}</Badge>
      {acces && (
        <>
          <p>{acces.email}</p>
          <p>
            {t('espaceCollectionneur.derniereConnexion', { date: date(acces.derniereConnexionLe) })}
          </p>
          {acces.motif && <p>{acces.motif}</p>}
        </>
      )}
      {ecartEmail && <p role="status">{t('espaceCollectionneur.ecartEmail')}</p>}
      {peutEcrire && (
        <div className={styles.gestes}>
          {(!acces || acces.etat === 'revoque') && (
            <>
              <Button
                disabled={!emails.length}
                loading={invitation.isPending}
                aria-describedby={!emails.length ? `${id}-email` : undefined}
                onClick={() => invitation.mutate()}
              >
                {t('espaceCollectionneur.inviter')}
              </Button>
              {!emails.length && <p id={`${id}-email`}>{t('espaceCollectionneur.emailRequis')}</p>}
            </>
          )}
          {acces && (acces.etat === 'invite' || acces.etat === 'actif') && (
            <>
              <Button
                variant="secondary"
                loading={action.isPending}
                onClick={() => action.mutate({ geste: 'renvoyer' })}
              >
                {t('espaceCollectionneur.renvoyer')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  action.reset();
                  setMotif('');
                  setGeste('suspendre');
                }}
              >
                {t('espaceCollectionneur.suspendre')}
              </Button>
            </>
          )}
          {acces?.etat === 'suspendu' && (
            <Button
              loading={action.isPending}
              onClick={() => action.mutate({ geste: 'reactiver' })}
            >
              {t('espaceCollectionneur.reactiver')}
            </Button>
          )}
          {acces && acces.etat !== 'revoque' && (
            <Button
              variant="secondary"
              onClick={() => {
                action.reset();
                setMotif('');
                setConfirme(false);
                setGeste('revoquer');
              }}
            >
              {t('espaceCollectionneur.revoquer')}
            </Button>
          )}
          <Button variant="secondary" onClick={() => setPartager(true)}>
            {t('espaceCollectionneur.partager')}
          </Button>
        </div>
      )}
      {Boolean(erreur) && !geste && (
        <p role="alert">
          {estApiError(erreur) && erreur.status === 409
            ? t('espaceCollectionneur.conflit')
            : t('commun:erreurReseau')}
        </p>
      )}
      {Boolean(erreur) && !geste && (
        <Button
          variant="secondary"
          onClick={() => {
            invitation.reset();
            action.reset();
            retrait.reset();
            void contact.refetch();
          }}
        >
          {t('espaceCollectionneur.recharger')}
        </Button>
      )}
      <h3>{t('espaceCollectionneur.partages')}</h3>
      {!partages.length && <p>{t('espaceCollectionneur.aucunPartage')}</p>}
      {[...groupes].map(([oeuvreId, elements]) => {
        const actifs = elements.filter((p) => !p.retireLe),
          retires = elements.filter((p) => p.retireLe);
        const titre = partages.find((p) => p.type === 'oeuvre' && p.cibleId === oeuvreId)?.libelle;
        return (
          <section key={oeuvreId} className={styles.bloc}>
            <h4>
              {oeuvreId
                ? (titre ?? t('espaceCollectionneur.oeuvreSansTitre'))
                : t('espaceCollectionneur.horsOeuvre')}
            </h4>
            {liste(actifs)}
            {retires.length > 0 && (
              <details>
                <summary>{t('espaceCollectionneur.retires', { count: retires.length })}</summary>
                {liste(retires)}
              </details>
            )}
          </section>
        );
      })}
      {partager && <PartagerDialog contactId={contactId} onFermer={() => setPartager(false)} />}
      {geste && (
        <Dialog
          open
          title={t(`espaceCollectionneur.${geste}`)}
          description={t('espaceCollectionneur.effetCoupure')}
          closeLabel={t('commun:actions.fermer')}
          onOpenChange={(o) => {
            if (!o) setGeste(null);
          }}
        >
          <form
            className={styles.formulaire}
            onSubmit={(e) => {
              e.preventDefault();
              action.mutate({ geste, motif: motif.trim() }, { onSuccess: () => setGeste(null) });
            }}
          >
            <Field htmlFor={`${id}-motif`} label={t('espaceCollectionneur.motif')}>
              <Textarea
                id={`${id}-motif`}
                required
                maxLength={1000}
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
              />
            </Field>
            {geste === 'revoquer' && (
              <label>
                <input
                  type="checkbox"
                  checked={confirme}
                  onChange={(e) => setConfirme(e.target.checked)}
                />{' '}
                {t('espaceCollectionneur.confirmerRevocation')}
              </label>
            )}
            <Button
              type="submit"
              disabled={!motif.trim() || (geste === 'revoquer' && !confirme)}
              loading={action.isPending}
            >
              {t(`espaceCollectionneur.${geste}`)}
            </Button>
            {action.isError && (
              <>
                <p role="alert">
                  {estApiError(action.error) && action.error.status === 409
                    ? t('espaceCollectionneur.conflit')
                    : t('commun:erreurReseau')}
                </p>
                <Button
                  variant="secondary"
                  onClick={() => {
                    void contact.refetch();
                    setGeste(null);
                    action.reset();
                  }}
                >
                  {t('espaceCollectionneur.recharger')}
                </Button>
              </>
            )}
          </form>
        </Dialog>
      )}
    </section>
  );
}
